import React from "react";
import axios from "axios";
import {
  Checkbox,
  Divider,
  message,
  Drawer,
  InputNumber,
  Row,
  Tag,
} from "antd";
import { useState, useEffect } from "react";
import { InfoCircleOutlined } from "@ant-design/icons";
import { difference } from "lodash";

import { BETA_FEATURES } from "../../constants/betaFeatures";

import { PERMISSIONS } from "../../constants/roles";
import { usePermissions } from "../App/PermissionContext"; 

const CheckboxGroup = Checkbox.Group;
const betaFeatureOptions = Object.values(BETA_FEATURES).map(feature => feature.label);

const UserBetaFeatures = ({
  userId,
  betaFeatures,
  dhlCodLimit,
  inheritedFeatures = [],
  inheritsFeatures = false,
  master,
  onFeaturesChange = () => {},
}) => {
  const { permissions } = usePermissions();
  
  const [checkedList, setCheckedList] = useState(betaFeatures);
  const [inherited, setInherited] = useState(inheritedFeatures);
  const [followsMaster, setFollowsMaster] = useState(inheritsFeatures);
  const [codLimit, setCodLimit] = useState(dhlCodLimit);
  const [savedCodLimit, setSavedCodLimit] = useState(dhlCodLimit);
  const [disabled, setDisabled] = useState(true);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDisabled(!permissions?.includes(PERMISSIONS.UPDATE_BETA_FEATURES));
  }, [permissions]);

  const checkAll = betaFeatureOptions.every((feature) =>
    checkedList.includes(feature)
  );

  const showFeatures = (user) => {
    const features = user.betaFeatures || [];
    setCheckedList(features);
    setInherited(user.inheritedFeatures || []);
    setFollowsMaster(!!user.inheritsFeatures);
    onFeaturesChange(features);
  };

  const showSaved = (savedUser, sentCodLimit) => {
    showFeatures(savedUser);
    if (sentCodLimit !== undefined) setSavedCodLimit(sentCodLimit);
    message.info("Updated");
  };

  const reloadFromServer = async () => {
    try {
      const response = await axios.get(`/api/users/all-user?userId=${userId}`);
      const user = response.data.data[0];
      const limit = user.merchant?.preferences?.dhlCodLimit ?? null;
      showFeatures(user);
      setCodLimit(limit);
      setSavedCodLimit(limit);
    } catch (err) {
      message.error(err.response?.data?.message || err.message);
    }
  };

  const codLimitFor = (added, removed) => {
    if (added.includes("DHL_COD_LIMIT")) return 2000;
    if (removed.includes("DHL_COD_LIMIT")) return null;
    return undefined;
  };

  const saveChange = async (added, removed, dhlCodLimitToSave) => {
    setSaving(true);
    try {
      const response = await axios.put(`/api/user/${userId}/beta-features`, {
        added,
        removed,
        dhlCodLimit: dhlCodLimitToSave,
      });
      showSaved(response.data.data, dhlCodLimitToSave);
    } catch (err) {
      message.error(err.response?.data?.message || err.message);
      await reloadFromServer();
    } finally {
      setSaving(false);
    }
  };

  const saveList = (list) => {
    const shownFeatures = checkedList.filter((feature) =>
      betaFeatureOptions.includes(feature)
    );
    const added = difference(list, shownFeatures);
    const removed = difference(shownFeatures, list);
    const limitToSave = codLimitFor(added, removed);

    setCheckedList(list);
    if (limitToSave !== undefined) setCodLimit(limitToSave);

    saveChange(added, removed, limitToSave);
  };

  const onCheckAllChange = (e) =>
    saveList(e.target.checked ? betaFeatureOptions : []);

  const saveCodLimit = () => {
    if (codLimit !== savedCodLimit) saveChange([], [], codLimit);
  };

  const label = `${checkAll ? "Disable All" : "Enable All"}`;
  return (
    <>
      <React.Fragment>
        Description <InfoCircleOutlined onClick={() => setDrawerVisible(true)} />
        <Drawer
          title="Features"
          placement="right"
          onClose={() => setDrawerVisible(false)}
          visible={drawerVisible}
          width={736}
        >
          {Object.values(BETA_FEATURES).map(feature => (
            <div className="mb-2">
              <strong>{feature.label}</strong>
              <br />
              {feature.desc}
            </div>
          ))}
        </Drawer>
      </React.Fragment>

      <Divider />
      <Checkbox
        checked={checkAll}
        onChange={onCheckAllChange}
        disabled={disabled || saving}
      >
        {label}
      </Checkbox>
      <Divider />
      {master && followsMaster && (
        <p>
          Inherited from <a href={`/user/${master.id}`}>{master.email}</a>
        </p>
      )}
      <CheckboxGroup
        options={betaFeatureOptions.map((feature) => ({
          value: feature,
          label: inherited.includes(feature) ? (
            <>
              {feature} <Tag color="blue">Inherited</Tag>
            </>
          ) : (
            feature
          ),
        }))}
        value={checkedList}
        onChange={saveList}
        disabled={disabled || saving}
      />
      {checkedList.includes("DHL_COD_LIMIT") && (
        <Row style={{ marginTop: 16 }} align="middle" >
          <label>DHL COD Limit (RM):</label>
          <InputNumber
            min={0}
            value={codLimit}
            onChange={(value) => setCodLimit(value)}
            disabled={disabled || saving}
            style={{ width: 200, marginLeft: 12 }}
            onBlur={saveCodLimit}
          />
        </Row>
      )}
    </>
  );
};

export default UserBetaFeatures;
